<script setup lang="ts">
import { ref, onMounted, computed } from 'vue'
import { useAccountSettingsStore } from '@/stores/accountSettings'
import { useProductSettingsStore } from '@/stores/productSettings'
import { getA5DebugStats, getA5VinComparison } from '@/api'

const accountStore = useAccountSettingsStore()
const productSettings = useProductSettingsStore()
const endpoint = import.meta.env.VITE_ISE_BACKEND_URL

const loading = ref(false)
const extraLoading = ref(false)
const missingLoading = ref(false)
const error = ref<string | null>(null)
const debugResult = ref<any | null>(null)
const extraRows = ref<any[]>([])
const missingRows = ref<any[]>([])
const showRaw = ref(false)
const compareRequestSeq = ref(0)
const extraRequestSeq = ref(0)
const missingRequestSeq = ref(0)

type DebugDiff = 'extraInA5' | 'missingInA5'

const nextCompareRequestSeq = () => {
  compareRequestSeq.value += 1
  return compareRequestSeq.value
}

const nextExtraRequestSeq = () => {
  extraRequestSeq.value += 1
  return extraRequestSeq.value
}

const nextMissingRequestSeq = () => {
  missingRequestSeq.value += 1
  return missingRequestSeq.value
}

const isLatestCompareRequest = (requestSeq: number) => requestSeq === compareRequestSeq.value

const isLatestExtraRequest = (requestSeq: number) => requestSeq === extraRequestSeq.value

const isLatestMissingRequest = (requestSeq: number) => requestSeq === missingRequestSeq.value

const formatUsd = (value: unknown) => {
  const n =
    typeof value === 'number'
      ? value
      : typeof value === 'string'
        ? Number(value)
        : NaN

  if (!Number.isFinite(n)) return '—'

  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(n)
}

const formatIso = (value: unknown) => {
  if (typeof value !== 'string' || !value.trim()) return '—'
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return '—'
  return d.toLocaleString()
}

const hasNoHistory = (row: unknown): boolean => {
  return (row as any)?.hasHistory === false
}

const tooltipText = (
  kind: 'added' | 'removed' | 'updated',
  row: unknown,
  value: unknown,
): string => {
  if (typeof value === 'string' && value.trim()) {
    const label =
      kind === 'added'
        ? 'Latest ADDED/LISTED'
        : kind === 'removed'
          ? 'Latest REMOVED/DELISTED'
          : 'Latest CHANGED'
    return `${label}: ${formatIso(value)}`
  }

  if (hasNoHistory(row)) {
    return 'ISE has not encountered this VIN before (no VehicleStatusHistory record).'
  }

  return kind === 'added'
    ? 'No ADDED/LISTED event found for this VIN.'
    : kind === 'removed'
      ? 'No REMOVED/DELISTED event found for this VIN.'
      : 'No CHANGED event found for this VIN.'
}

// Inputs
const jazelId = ref(accountStore.accountId)
const selectedA5Ids = ref<number[]>([])
const condition = ref<'new' | 'used' | 'other' | undefined>(undefined)
const rowsPerPage = ref(25)
const extraPage = ref(1)
const missingPage = ref(1)

// Tracks the server page that has actually been loaded.
// Do not rely on v-model page refs for change detection because Vuetify may update
// them before @update:options runs.
const loadedRowsPerPage = ref(rowsPerPage.value)
const loadedExtraPage = ref(1)
const loadedMissingPage = ref(1)

const selectedMakes = ref<string[]>([])

const makeItems = computed(() => {
  const makeMap = new Map<string, number>()
  const accounts = debugResult.value?.A5?.accounts
  const facetSources = Array.isArray(accounts) && accounts.length ? accounts : [debugResult.value?.A5]
  for (const result of facetSources) {
    const buckets = result?.facets?.make_sidebar?.buckets
    if (!Array.isArray(buckets)) continue
    for (const b of buckets) {
      const title = String(b?.value ?? '').trim()
      const count = typeof b?.count === 'number' ? b.count : 0
      const value = title.toLowerCase()
      if (!title || !value) continue
      makeMap.set(value, (makeMap.get(value) ?? 0) + count)
    }
  }
  return [...makeMap.entries()]
    .sort((a, b) => a[0].localeCompare(b[0]))
    .map(([value, count]) => ({
      title: `${value.charAt(0).toUpperCase() + value.slice(1)} (${count})`,
      value,
    }))
})

const combinedA6Count = computed(() => {
  return debugResult.value?.counts?.A6 ?? null
})

const combinedA5Count = computed(() => {
  return debugResult.value?.counts?.A5 ?? 0
})

const conditionBreakdown = computed(() => {
  const breakdown = debugResult.value?.conditionBreakdown
  return breakdown && typeof breakdown === 'object' ? breakdown : null
})

const otherConditionCount = computed(() => {
  const count = conditionBreakdown.value?.other
  return typeof count === 'number' ? count : 0
})

type ConditionFilterValue = '' | 'new' | 'used' | 'other'

const conditionFilter = computed({
  get: (): ConditionFilterValue => condition.value ?? '',
  set: (value: ConditionFilterValue) => {
    condition.value = value === '' ? undefined : value
  },
})

const conditionItems = computed(() => {
  const items: Array<{ title: string; value: ConditionFilterValue }> = [
    { title: 'All', value: '' },
    { title: 'New', value: 'new' },
    { title: 'Used', value: 'used' },
  ]

  if (otherConditionCount.value > 0 || condition.value === 'other') {
    const count = otherConditionCount.value
    items.push({
      title: count > 0 ? `Other / Unknown (${count})` : 'Other / Unknown',
      value: 'other',
    })
  }

  return items
})

const combinedExtraInA5 = computed(() => {
  return {
    count: debugResult.value?.diff?.extraInA5?.count ?? 0,
    items: extraRows.value,
  }
})

const combinedMissingInA5 = computed(() => {
  return {
    count: debugResult.value?.diff?.missingInA5?.count ?? 0,
    items: missingRows.value,
  }
})

const primaryFeed = computed(() => {
  const raws = (productSettings as any)?.primarySources
  if (!Array.isArray(raws) || raws.length === 0) return null

  const feeds = raws
    .map((s: unknown) => String(s || '').trim())
    .filter(Boolean)
    .map((s: string) => s.toLowerCase().replace(/^import_/, '')) // accept import_vauto etc.

  return feeds.length ? feeds.join(',') : null
})

const possibleA5Ids = ref<number[]>([])

onMounted(() => {
  const inventoryIds = accountStore.inventoryIds

  if (Array.isArray(inventoryIds)) {
    possibleA5Ids.value = inventoryIds
    selectedA5Ids.value = inventoryIds.length ? [inventoryIds[0]] : []
  } else if (typeof inventoryIds === 'number') {
    possibleA5Ids.value = [inventoryIds]
    selectedA5Ids.value = [inventoryIds]
  }

  if (selectedA5Ids.value.length) {
    fetchAllComparisons()
  }
})

const fetchAllComparisons = async () => {
  if (!selectedA5Ids.value.length) {
    error.value = 'Select at least one A5 ID'
    return
  }
  const requestSeq = nextCompareRequestSeq()
  nextExtraRequestSeq()
  nextMissingRequestSeq()
  loading.value = true
  extraLoading.value = true
  missingLoading.value = true
  error.value = null
  extraPage.value = 1
  missingPage.value = 1
  loadedExtraPage.value = 1
  loadedMissingPage.value = 1
  loadedRowsPerPage.value = rowsPerPage.value
  debugResult.value = null
  extraRows.value = []
  missingRows.value = []

  try {
    const params: any = {
      a5Ids: selectedA5Ids.value,
      page: 1,
      pageSize: rowsPerPage.value,
      diff: 'all',
      makes: selectedMakes.value,
    }
    if (condition.value) params.condition = condition.value
    if (primaryFeed.value) params.primaryFeed = primaryFeed.value

    const response = await getA5DebugStats(endpoint, jazelId.value, params)
    if (!isLatestCompareRequest(requestSeq)) return

    debugResult.value = response
    extraRows.value = response?.diff?.extraInA5?.items ?? response?.diff?.extraInA5?.sample ?? []
    missingRows.value = response?.diff?.missingInA5?.items ?? response?.diff?.missingInA5?.sample ?? []
  } catch (e: any) {
    if (!isLatestCompareRequest(requestSeq)) return
    console.error(e)
    error.value = e.message || 'Failed to fetch debug data'
  } finally {
    if (isLatestCompareRequest(requestSeq)) {
      loading.value = false
      extraLoading.value = false
      missingLoading.value = false
    }
  }
}

const fetchDebugPage = async (
  diff: DebugDiff,
  options: { setGlobalLoading?: boolean } = {},
) => {
  const setGlobalLoading = options.setGlobalLoading ?? true
  if (!selectedA5Ids.value.length) return
  const requestSeq =
    diff === 'extraInA5' ? nextExtraRequestSeq() : nextMissingRequestSeq()
  const isLatestRequest =
    diff === 'extraInA5' ? isLatestExtraRequest : isLatestMissingRequest
  if (setGlobalLoading) error.value = null

  const loadingRef = diff === 'extraInA5' ? extraLoading : missingLoading
  loadingRef.value = true
  if (setGlobalLoading) loading.value = true

  try {
    const params: any = {
      a5Ids: selectedA5Ids.value,
      page: diff === 'extraInA5' ? extraPage.value : missingPage.value,
      pageSize: rowsPerPage.value,
      diff,
      makes: selectedMakes.value,
    }
    if (condition.value) params.condition = condition.value
    if (primaryFeed.value) params.primaryFeed = primaryFeed.value

    const response = await getA5DebugStats(endpoint, jazelId.value, params)
    if (!isLatestRequest(requestSeq)) return

    debugResult.value = response

    const rows = response?.diff?.[diff]?.items ?? response?.diff?.[diff]?.sample ?? []
    if (diff === 'extraInA5') {
      extraRows.value = rows
    } else {
      missingRows.value = rows
    }
  } catch (e: any) {
    if (!isLatestRequest(requestSeq)) return
    console.error(e)
    error.value = e.message || 'Failed to fetch debug data'
  } finally {
    loadingRef.value = false
    if (setGlobalLoading) loading.value = false
  }
}

const handleExtraOptions = async (options: { page?: number; itemsPerPage?: number }) => {
  if (!debugResult.value) return

  const nextPage = options.page ?? extraPage.value
  const nextRowsPerPage = options.itemsPerPage ?? rowsPerPage.value

  const changedPage = loadedExtraPage.value !== nextPage
  const changedItemsPerPage = loadedRowsPerPage.value !== nextRowsPerPage

  if (!changedPage && !changedItemsPerPage) return

  if (changedItemsPerPage) {
    rowsPerPage.value = nextRowsPerPage
    loadedRowsPerPage.value = nextRowsPerPage

    extraPage.value = 1
    missingPage.value = 1
    loadedExtraPage.value = 1
    loadedMissingPage.value = 1

    await fetchAllComparisons()
    return
  }

  extraPage.value = nextPage
  loadedExtraPage.value = nextPage

  await fetchDebugPage('extraInA5', { setGlobalLoading: false })
}

const handleMissingOptions = async (options: { page?: number; itemsPerPage?: number }) => {
  if (!debugResult.value) return

  const nextPage = options.page ?? missingPage.value
  const nextRowsPerPage = options.itemsPerPage ?? rowsPerPage.value

  const changedPage = loadedMissingPage.value !== nextPage
  const changedItemsPerPage = loadedRowsPerPage.value !== nextRowsPerPage

  if (!changedPage && !changedItemsPerPage) return

  if (changedItemsPerPage) {
    rowsPerPage.value = nextRowsPerPage
    loadedRowsPerPage.value = nextRowsPerPage

    extraPage.value = 1
    missingPage.value = 1
    loadedExtraPage.value = 1
    loadedMissingPage.value = 1

    await fetchAllComparisons()
    return
  }

  missingPage.value = nextPage
  loadedMissingPage.value = nextPage

  await fetchDebugPage('missingInA5', { setGlobalLoading: false })
}

// --- Single-VIN comparison ---
const showVinCompare = ref(false)
const vinCompareLoading = ref(false)
const vinCompareError = ref<string | null>(null)
const vinCompareResult = ref<any | null>(null)
const vinCompareVin = ref('')
const showOnlyDiffs = ref(false)

const filteredComparison = computed(() => {
  const rows = vinCompareResult.value?.comparison ?? []
  if (!showOnlyDiffs.value) return rows
  return rows.filter((r: any) => !r.match)
})

const openVinComparison = async (vin: string) => {
  if (!vin?.trim() || !selectedA5Ids.value.length) return

  vinCompareVin.value = vin.trim().toUpperCase()
  vinCompareError.value = null
  vinCompareResult.value = null
  showVinCompare.value = true
  vinCompareLoading.value = true
  showOnlyDiffs.value = false

  try {
    const result = await getA5VinComparison(endpoint, jazelId.value, vinCompareVin.value, {
      a5Ids: selectedA5Ids.value,
    })
    vinCompareResult.value = result
  } catch (e: any) {
    console.error(e)
    vinCompareError.value = e.message || 'Failed to fetch VIN comparison'
  } finally {
    vinCompareLoading.value = false
  }
}

const formatCompareValue = (val: unknown): string => {
  if (val === null || val === undefined) return '—'
  if (Array.isArray(val)) return val.join(', ')
  if (typeof val === 'object') return JSON.stringify(val)
  return String(val)
}

const headersMissing = [
  { title: '', key: 'actions', sortable: false, width: '40px' },
  { title: 'VIN', key: 'vin' },
  { title: 'Source', key: 'membershipSource', sortable: false },
  { title: 'Added', key: 'addedAt', sortable: false },
  { title: 'Removed', key: 'removedAt', sortable: false },
  { title: 'Updated', key: 'lastUpdatedAt', sortable: false },
  { title: 'State', key: 'state' },
  { title: 'Make', key: 'make' },
  { title: 'Model', key: 'model' },
  { title: 'Condition', key: 'condition' },
]


const headersExtra = [
  { title: '', key: 'actions', sortable: false, width: '40px' },
  { title: 'VIN', key: 'vin' },
  { title: 'A5 Account', key: 'a5Id' },
  { title: 'On Primary Feeds', key: 'onPrimaryFeed', sortable: false },
  { title: 'Added', key: 'addedAt', sortable: false },
  { title: 'Removed', key: 'removedAt', sortable: false },
  { title: 'Updated', key: 'lastUpdatedAt', sortable: false },
  { title: 'State', key: 'state' },
  { title: 'Make', key: 'make' },
  { title: 'Model', key: 'model' },
  { title: 'Condition', key: 'condition' },
]

</script>

<template>
  <div class="h-100">
        <v-row align="center">
            <v-col cols="12" md="2">
                <div class="text-caption text-grey">Jazel ID (JUID)</div>
                <div class="text-body-1 font-weight-bold">{{ jazelId }}</div>
            </v-col>
            <v-col cols="12" md="3">
                <div class="text-caption text-grey">A5 Account IDs</div>
                <div v-if="possibleA5Ids.length <= 1" class="text-body-1 font-weight-bold">{{ selectedA5Ids[0] ?? '—' }}</div>
                <v-select
                  v-else
                  v-model="selectedA5Ids"
                  :items="possibleA5Ids"
                  multiple
                  chips
                  closable-chips
                  variant="underlined"
                  density="compact"
                  :disabled="loading"
                  hide-details
                  class="mt-0 pt-0"
                ></v-select>
            </v-col>
            <v-col cols="12" md="2">
                <div class="text-caption text-grey">Condition</div>
                <v-select
                  v-model="conditionFilter"
                  :items="conditionItems"
                  item-title="title"
                  item-value="value"
                  hide-details
                  density="compact"
                  variant="outlined"
                  :disabled="loading"
                />
            </v-col>
            <v-col cols="12" md="2">
                <div class="text-caption text-grey">Make</div>
                <v-select
                  v-model="selectedMakes"
                  :items="makeItems"
                  multiple
                  chips
                  closable-chips
                  clearable
                  hide-details
                  density="compact"
                  variant="outlined"
                  placeholder="All"
                  :disabled="loading"
                />
            </v-col>
            <v-col cols="12" md="2">
                <div class="text-caption text-grey">&nbsp;</div>
                <v-btn
                  color="primary"
                  :loading="loading"
                  :disabled="loading || !selectedA5Ids.length"
                  @click="fetchAllComparisons"
                >
                  Compare
                </v-btn>
            </v-col>
        </v-row>

        <v-alert v-if="error" type="error" class="mb-4">{{ error }}</v-alert>

        <!-- Loading state -->
        <div v-if="loading" class="d-flex justify-center my-8">
            <v-progress-circular indeterminate color="primary" size="48" width="3"></v-progress-circular>
        </div>

        <!-- Combined detail view -->
        <div v-else-if="debugResult">
            <!-- Combined count cards -->
            <v-row class="mb-4 align-stretch">
                <v-col cols="12" md="3" class="d-flex">
                    <v-card variant="tonal" color="info" class="flex-grow-1 d-flex align-center justify-center">
                        <v-card-text class="text-center">
                            <div class="text-h6">A6 Count</div>
                            <div class="text-h4">{{ combinedA6Count ?? '-' }}</div>
                        </v-card-text>
                    </v-card>
                </v-col>
                <v-col cols="12" md="3" class="d-flex">
                    <v-card variant="tonal" color="info" class="flex-grow-1 d-flex align-center justify-center">
                        <v-card-text class="text-center">
                            <div class="text-h6">Combined A5 Count</div>
                            <div class="text-h4">{{ combinedA5Count }}</div>
                            <div v-if="selectedA5Ids.length > 1" class="text-caption text-grey mt-1">
                              Union across {{ selectedA5Ids.length }} A5 accounts
                            </div>
                        </v-card-text>
                    </v-card>
                </v-col>
                <v-col cols="12" md="3" class="d-flex">
                    <v-card variant="tonal" :color="combinedExtraInA5.count === 0 ? 'success' : 'warning'" class="flex-grow-1 d-flex align-center justify-center">
                        <v-card-text class="text-center">
                            <div class="text-h6">Extra in A5 (Missing in A6)</div>
                            <div class="text-h4">{{ combinedExtraInA5.count }}</div>
                        </v-card-text>
                    </v-card>
                </v-col>
                <v-col cols="12" md="3" class="d-flex">
                    <v-card variant="tonal" :color="combinedMissingInA5.count === 0 ? 'success' : 'error'" class="flex-grow-1 d-flex align-center justify-center">
                        <v-card-text class="text-center">
                            <div class="text-h6">Extra in A6 (Missing in A5)</div>
                            <div class="text-h4">{{ combinedMissingInA5.count }}</div>
                        </v-card-text>
                    </v-card>
                </v-col>
            </v-row>

            <!-- Combined Extra in A5 table -->
            <v-row>
                <v-col cols="12">
                    <h3 class="text-h6 mb-2">Extra in A5 (Missing in A6)</h3>
                    <div class="table-scroll">
                    <v-data-table-server
                        :headers="headersExtra"
                        :items="combinedExtraInA5.items"
                        :items-length="combinedExtraInA5.count"
                        :loading="extraLoading"
                        v-model:page="extraPage"
                        v-model:items-per-page="rowsPerPage"
                        :items-per-page-options="[10, 25, 50, 100, 250, 500]"
                        density="compact"
                        class="elevation-1 debug-table"
                        @update:options="handleExtraOptions"
                      >
                      <template #item.actions="{ item }">
                        <v-tooltip location="top" text="Compare A5 vs A6 fields">
                          <template #activator="{ props }">
                            <v-btn icon size="x-small" variant="text" v-bind="props" @click="openVinComparison((item as any).vin)">
                              <v-icon size="16">mdi-file-compare</v-icon>
                            </v-btn>
                          </template>
                        </v-tooltip>
                      </template>
                      <template #item.onPrimaryFeed="{ item, value }">
                        <template v-if="(value ?? (item as any)?.inPrimary) === true">
                          <v-icon size="18" color="success">mdi-check</v-icon>
                        </template>
                        <template v-else-if="(value ?? (item as any)?.inPrimary) === false">
                          <v-icon size="18" color="error">mdi-close</v-icon>
                        </template>
                        <template v-else>
                          <v-icon size="18" color="grey">mdi-minus</v-icon>
                        </template>
                      </template>
                      <template #item.state="{ value }">
                        {{ value || '—' }}
                      </template>
                      <template #item.addedAt="{ item, value }">
                        <v-tooltip location="top">
                          <template #activator="{ props }">
                            <span v-bind="props" class="d-inline-flex align-center">
                              <template v-if="value">
                                {{ formatIso(value) }}
                              </template>
                              <v-icon v-else size="18" :color="hasNoHistory(item) ? 'warning' : 'grey'">
                                {{ hasNoHistory(item) ? 'mdi-help-circle-outline' : 'mdi-minus-circle-outline' }}
                              </v-icon>
                            </span>
                          </template>
                          <span>{{ tooltipText('added', item, value) }}</span>
                        </v-tooltip>
                      </template>
                      <template #item.removedAt="{ item, value }">
                        <v-tooltip location="top">
                          <template #activator="{ props }">
                            <span v-bind="props" class="d-inline-flex align-center">
                              <template v-if="value">
                                {{ formatIso(value) }}
                              </template>
                              <v-icon v-else size="18" :color="hasNoHistory(item) ? 'warning' : 'grey'">
                                {{ hasNoHistory(item) ? 'mdi-help-circle-outline' : 'mdi-minus-circle-outline' }}
                              </v-icon>
                            </span>
                          </template>
                          <span>{{ tooltipText('removed', item, value) }}</span>
                        </v-tooltip>
                      </template>
                      <template #item.lastUpdatedAt="{ item, value }">
                        <v-tooltip location="top">
                          <template #activator="{ props }">
                            <span v-bind="props" class="d-inline-flex align-center">
                              <template v-if="value">
                                {{ formatIso(value) }}
                              </template>
                              <v-icon v-else size="18" :color="hasNoHistory(item) ? 'warning' : 'grey'">
                                {{ hasNoHistory(item) ? 'mdi-help-circle-outline' : 'mdi-minus-circle-outline' }}
                              </v-icon>
                            </span>
                          </template>
                          <span>{{ tooltipText('updated', item, value) }}</span>
                        </v-tooltip>
                      </template>
                    </v-data-table-server>
                    </div>
                </v-col>
            </v-row>

            <!-- Combined Missing from ALL A5 accounts -->
            <v-row class="mt-4">
                <v-col cols="12">
                    <h3 class="text-h6 mb-2">
                      {{ selectedA5Ids.length > 1 ? 'Missing in Combined A5 Accounts (Present in A6)' : `Missing in A5: ${selectedA5Ids[0]} (Present in A6)` }}
                    </h3>
                    <div class="table-scroll">
                    <v-data-table-server
                        :headers="headersMissing"
                        :items="combinedMissingInA5.items"
                        :items-length="combinedMissingInA5.count"
                        :loading="missingLoading"
                        v-model:page="missingPage"
                        v-model:items-per-page="rowsPerPage"
                        :items-per-page-options="[10, 25, 50, 100, 250, 500]"
                        density="compact"
                        class="elevation-1 debug-table"
                        @update:options="handleMissingOptions"
                      >
                        <template #item.actions="{ item }">
                          <v-tooltip location="top" text="Compare A5 vs A6 fields">
                            <template #activator="{ props }">
                              <v-btn icon size="x-small" variant="text" v-bind="props" @click="openVinComparison((item as any).vin)">
                                <v-icon size="16">mdi-file-compare</v-icon>
                              </v-btn>
                            </template>
                          </v-tooltip>
                        </template>
                        <template #item.membershipSource="{ value }">
                          {{ value || '—' }}
                        </template>
                        <template #item.state="{ value }">
                          {{ value || '—' }}
                        </template>
                        <template #item.addedAt="{ item, value }">
                          <v-tooltip location="top">
                            <template #activator="{ props }">
                              <span v-bind="props" class="d-inline-flex align-center">
                                <template v-if="value">
                                  {{ formatIso(value) }}
                                </template>
                                <v-icon v-else size="18" :color="hasNoHistory(item) ? 'warning' : 'grey'">
                                  {{ hasNoHistory(item) ? 'mdi-help-circle-outline' : 'mdi-minus-circle-outline' }}
                                </v-icon>
                              </span>
                            </template>
                            <span>{{ tooltipText('added', item, value) }}</span>
                          </v-tooltip>
                        </template>
                        <template #item.removedAt="{ item, value }">
                          <v-tooltip location="top">
                            <template #activator="{ props }">
                              <span v-bind="props" class="d-inline-flex align-center">
                                <template v-if="value">
                                  {{ formatIso(value) }}
                                </template>
                                <v-icon v-else size="18" :color="hasNoHistory(item) ? 'warning' : 'grey'">
                                  {{ hasNoHistory(item) ? 'mdi-help-circle-outline' : 'mdi-minus-circle-outline' }}
                                </v-icon>
                              </span>
                            </template>
                            <span>{{ tooltipText('removed', item, value) }}</span>
                          </v-tooltip>
                        </template>
                        <template #item.lastUpdatedAt="{ item, value }">
                          <v-tooltip location="top">
                            <template #activator="{ props }">
                              <span v-bind="props" class="d-inline-flex align-center">
                                <template v-if="value">
                                  {{ formatIso(value) }}
                                </template>
                                <v-icon v-else size="18" :color="hasNoHistory(item) ? 'warning' : 'grey'">
                                  {{ hasNoHistory(item) ? 'mdi-help-circle-outline' : 'mdi-minus-circle-outline' }}
                                </v-icon>
                              </span>
                            </template>
                            <span>{{ tooltipText('updated', item, value) }}</span>
                          </v-tooltip>
                        </template>
                    </v-data-table-server>
                    </div>
                </v-col>
            </v-row>


            <v-divider class="my-6" />

            <div class="d-flex justify-space-between align-center">
              <div class="text-caption text-grey">
                Generated: {{ debugResult?.generatedAt ?? '—' }}
              </div>
              <v-btn variant="text" @click="showRaw = !showRaw">
                {{ showRaw ? 'Hide raw JSON' : 'Show raw JSON' }}
              </v-btn>
            </div>

            <v-expand-transition>
              <div v-if="showRaw" class="mt-3">
                <pre class="debug-pre">{{ JSON.stringify(debugResult, null, 2) }}</pre>
              </div>
            </v-expand-transition>
        </div>

    <!-- Single VIN comparison dialog -->
    <v-dialog v-model="showVinCompare" max-width="960" scrollable>
      <v-card>
        <v-card-title class="d-flex align-center justify-space-between">
          <span>VIN Comparison: {{ vinCompareVin }}</span>
          <v-btn icon size="small" variant="text" @click="showVinCompare = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text>
          <div v-if="vinCompareLoading" class="d-flex justify-center my-8">
            <v-progress-circular indeterminate color="primary" size="48" width="3" />
          </div>

          <v-alert v-else-if="vinCompareError" type="error" class="mb-4">{{ vinCompareError }}</v-alert>

          <template v-else-if="vinCompareResult">
            <v-alert v-if="!vinCompareResult.a5 && !vinCompareResult.a6" type="warning" class="mb-4">
              VIN not found in either A5 or A6.
            </v-alert>
            <v-alert v-else-if="!vinCompareResult.a5" type="info" class="mb-4">
              VIN not found in A5 CloudSearch. Only A6 data is shown.
            </v-alert>
            <v-alert v-else-if="!vinCompareResult.a6" type="info" class="mb-4">
              VIN not found in A6 OpenSearch. Only A5 data is shown.
            </v-alert>

            <div class="d-flex align-center justify-space-between mb-3">
              <div class="text-body-2">
                <strong>{{ vinCompareResult.diffCount }}</strong> of
                <strong>{{ vinCompareResult.totalFields }}</strong> fields differ
              </div>
              <v-switch
                v-model="showOnlyDiffs"
                label="Show only differences"
                hide-details
                density="compact"
                color="primary"
              />
            </div>

            <v-table density="compact" class="vin-compare-table">
              <colgroup>
                <col style="width: 180px" />
                <col style="width: calc(50% - 90px)" />
                <col style="width: calc(50% - 90px)" />
              </colgroup>
              <thead>
                <tr>
                  <th class="text-left">Field</th>
                  <th class="text-left">A5 Value</th>
                  <th class="text-left">A6 Value</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="row in filteredComparison"
                  :key="row.field"
                  :class="{ 'compare-diff-row': !row.match }"
                >
                  <td class="font-weight-medium text-body-2 field-cell">{{ row.field }}</td>
                  <td class="text-body-2 value-cell" :class="{ 'compare-diff-cell': !row.match && row.a5Value != null }">
                    <div class="value-content" :title="formatCompareValue(row.a5Value)">{{ formatCompareValue(row.a5Value) }}</div>
                  </td>
                  <td class="text-body-2 value-cell" :class="{ 'compare-diff-cell': !row.match && row.a6Value != null }">
                    <div class="value-content" :title="formatCompareValue(row.a6Value)">{{ formatCompareValue(row.a6Value) }}</div>
                  </td>
                </tr>
                <tr v-if="filteredComparison.length === 0">
                  <td colspan="3" class="text-center text-grey pa-4">
                    {{ showOnlyDiffs ? 'No differences found — all fields match.' : 'No fields to compare.' }}
                  </td>
                </tr>
              </tbody>
            </v-table>

            <div class="text-caption text-grey mt-3">
              Generated: {{ vinCompareResult.generatedAt ?? '—' }}
            </div>
          </template>
        </v-card-text>
      </v-card>
    </v-dialog>
  </div>
</template>

<style scoped>
.table-scroll {
  max-width: 100%;
  overflow-x: auto;
}

.debug-table {
  min-width: 980px;
}

.debug-pre {
  max-height: 520px;
  overflow: auto;
  padding: 12px;
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 8px;
  background: rgba(0, 0, 0, 0.03);
  font-size: 12px;
  line-height: 1.4;
}

.vin-compare-table {
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 8px;
  table-layout: fixed;
  width: 100%;
}

.field-cell {
  word-break: break-word;
}

.value-cell {
  padding: 4px 8px !important;
}

.value-content {
  max-height: 4.5em;
  overflow: hidden;
  text-overflow: ellipsis;
  word-break: break-word;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  cursor: default;
}

.compare-diff-row {
  background-color: rgba(255, 235, 59, 0.12) !important;
}

.compare-diff-cell {
  font-weight: 600;
  color: #e65100;
}

</style>
